/**
 * Two-step sign-in (an authenticator app code) for Admin and Superadmin
 * accounts: the calls the provider page makes to the operations server
 * (vrs-ops-server/src/routes/admin-totp-routes.ts), and the checks on what
 * comes back. Kept free of the page so it can be tested.
 *
 * Two ways in:
 * - a sign-in that must set up the step first carries an enrolment ticket;
 * - a signed-in admin uses the admin session (vrs_admin_token).
 */
const RECOVERY_CODE = /^[a-f0-9]{6}-[a-f0-9]{6}$/;
const OFFLINE = 'No connection. Try again.';
const INCOMPLETE = 'The server answer was not complete. Try again.';
function failure(status, error) {
    return { error, ok: false, status };
}
async function send(fetchImpl, base, path, auth, body) {
    const headers = { 'Content-Type': 'application/json' };
    const payload = { ...body };
    if (auth && 'token' in auth)
        headers.Authorization = `Bearer ${auth.token}`;
    if (auth && 'ticket' in auth)
        payload.enrollmentTicket = auth.ticket;
    try {
        const response = await fetchImpl(`${base}${path}`, {
            method: body === undefined && !(auth && 'ticket' in auth) ? 'GET' : 'POST',
            headers,
            ...(body === undefined && !(auth && 'ticket' in auth) ? {} : { body: JSON.stringify(payload) })
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            return failure(response.status, typeof data?.error === 'string' && data.error ? data.error : OFFLINE);
        }
        return { data, ok: true };
    }
    catch {
        return failure(null, OFFLINE);
    }
}
/** The 10 recovery codes of an answer, or null when the answer does not hold exactly them. */
export function readRecoveryCodes(data) {
    const codes = Array.isArray(data?.recoveryCodes) ? data.recoveryCodes : null;
    return codes && codes.length === 10 && codes.every((code) => typeof code === 'string' && RECOVERY_CODE.test(code))
        ? codes : null;
}
/** The key in groups of four, as people type it into an authenticator app. */
export function groupKey(secret) {
    return String(secret || '').replace(/(.{4})/g, '$1 ').trim();
}
export async function loadTwoStepStatus(fetchImpl, base, token) {
    const result = await send(fetchImpl, base, '', { token });
    if (!result.ok)
        return result;
    const { data } = result;
    return typeof data?.enabled === 'boolean'
        ? { enabled: data.enabled, ok: true, remainingRecoveryCodes: Number(data.remainingRecoveryCodes) || 0, required: data.required === true }
        : failure(200, INCOMPLETE);
}
/** A new secret: the QR code and the key. A session also needs the password. */
export async function startTwoStep(fetchImpl, base, auth, password) {
    const result = await send(fetchImpl, base, '/enroll/start', auth, 'token' in auth ? { password: password || '' } : {});
    if (!result.ok)
        return result;
    const { otpauthUri, secret } = result.data || {};
    return typeof secret === 'string' && /^[A-Z2-7]{16,}$/.test(secret)
        && typeof otpauthUri === 'string' && otpauthUri.startsWith('otpauth://totp/')
        ? { ok: true, otpauthUri, secret }
        : failure(200, INCOMPLETE);
}
/**
 * The first code. Gives the 10 recovery codes; with a ticket, also the
 * session (`session` is the whole sign-in answer, token and user).
 */
export async function verifyTwoStep(fetchImpl, base, auth, code) {
    const result = await send(fetchImpl, base, '/enroll/verify', auth, { code: code.trim() });
    if (!result.ok)
        return result;
    const recoveryCodes = readRecoveryCodes(result.data);
    if (!recoveryCodes)
        return failure(200, INCOMPLETE);
    if ('ticket' in auth && (typeof result.data?.token !== 'string' || !result.data.token || !result.data.user)) {
        return failure(200, INCOMPLETE);
    }
    return { ok: true, recoveryCodes, session: 'ticket' in auth ? result.data : null };
}
export async function replaceRecoveryCodes(fetchImpl, base, token, password, code) {
    const result = await send(fetchImpl, base, '/recovery-codes', { token }, { code: code.trim(), password });
    if (!result.ok)
        return result;
    const recoveryCodes = readRecoveryCodes(result.data);
    return recoveryCodes ? { ok: true, recoveryCodes } : failure(200, INCOMPLETE);
}
export async function turnOffTwoStep(fetchImpl, base, token, password, code) {
    const result = await send(fetchImpl, base, '/disable', { token }, { code: code.trim(), password });
    if (!result.ok)
        return result;
    return result.data?.enabled === false ? { ok: true } : failure(200, INCOMPLETE);
}
